import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { pageTitle, canonical } from "@/lib/seo";

/**
 * DRB-72 (SEO) — the single owner of a page's head.
 *
 * WHAT WAS WRONG
 * --------------
 * Every route hand-rolled its own <Helmet> with a title, a description and a canonical, and NOT
 * ONE of them set an og: or twitter: tag. Those lived only in index.html, hardcoded to the
 * homepage — so measured live on 2026-09-01, all 22 prerendered routes carried an identical
 * og:title, og:description and `og:url = https://drburnsreleaf.com/`. Every share of every
 * product page on Facebook, LinkedIn, iMessage, Slack or WhatsApp unfurled as the homepage.
 *
 * Worse, react-helmet-async only manages nodes it tagged with data-rh and APPENDS rather than
 * replaces, so index.html's static <meta name="description"> was never removed. 4 of 5 sampled
 * routes served TWO conflicting descriptions, and the prerender then baked both into the static
 * file a crawler reads. /contact served only the generic one, because it set no description at all.
 *
 * WHY A COMPONENT AND NOT TEN COPIES
 * ----------------------------------
 * Adding og tags to each route by hand is what produced this: ten independent heads, no owner, and
 * a description that one route could silently forget. Here `description` is REQUIRED by the type,
 * `path` produces both the canonical and og:url so they cannot disagree, and every social tag is
 * derived rather than restated. index.html now carries none of these, so nothing can duplicate.
 *
 * The prerender picks all of this up for free: it snapshots document.head.innerHTML after helmet
 * has run, so whatever this sets is what a crawler gets in the static file.
 */

const OG_IMAGE = "https://drburnsreleaf.com/brand/og-image.png";

type Props = {
  /** Page title WITHOUT the site suffix; `pageTitle` adds it. Omit on the home page. */
  title?: string;
  /**
   * Required. A page with no description falls through to the generic site one, which is how
   * /contact ended up describing the homepage.
   */
  description: string;
  /** Route path, e.g. "/shop" or `/products/${slug}`. Drives canonical AND og:url together. */
  path: string;
  /** Absolute URL. Defaults to the brand card built under DRB-66. */
  image?: string;
  type?: "website" | "article" | "product";
  /** Thin or private pages. Adds robots noindex, matching search/not-found/account. */
  noindex?: boolean;
  /** JSON-LD and anything else that belongs in this page's head. */
  children?: ReactNode;
};

export function Seo({
  title,
  description,
  path,
  image = OG_IMAGE,
  type = "website",
  noindex,
  children,
}: Props) {
  const url = canonical(path);
  const fullTitle = pageTitle(title);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="DrBurns’ ReLeaf" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {children}
    </Helmet>
  );
}
